import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  Box,
  Heading,
  Text,
  Input,
  Button,
  Progress,
  Icon,
  useToast,
} from '@chakra-ui/react';
import { FiZap, FiArrowRight } from 'react-icons/fi';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SellerProfileCard from './SellerProfileCard';
import api from '../../services/api';

const inputProps = {
  bg: '#171717',
  borderColor: 'rgba(255,255,255,0.12)',
  color: '#fafafa',
  fontSize: 'sm',
  _placeholder: { color: 'rgba(255,255,255,0.25)' },
  _hover: { borderColor: 'rgba(255,255,255,0.2)' },
  _focus: { borderColor: 'rgba(255,255,255,0.35)', boxShadow: 'none' },
};

const DISMISSED_KEY = 'seller_profile_setup_dismissed';
const TRIGGER_KEY = 'show_seller_profile_setup';

export default function SellerProfileSetupModal() {
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const profileCardRef = useRef(null);
  const [continuing, setContinuing] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState('inputs'); // 'inputs' | 'generating' | 'review'
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (isOpen) return;

    // Only open for brand-new registrations. The flag is written by RegisterPage
    // immediately before navigating, so it's always present for new users and
    // never present for returning users logging in.
    if (sessionStorage.getItem(TRIGGER_KEY)) {
      sessionStorage.removeItem(TRIGGER_KEY);
      sessionStorage.removeItem(DISMISSED_KEY);
      setIsOpen(true);
    }
  // Re-run on location change so the flag is caught even if user was already
  // authenticated when RegisterPage called navigate('/').
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname]);

  const handleDismiss = useCallback(() => {
    sessionStorage.setItem(DISMISSED_KEY, '1');
    setIsOpen(false);
    navigate('/campaign/new?welcome=true');
  }, [navigate]);

  const handleContinue = useCallback(async () => {
    setContinuing(true);
    try {
      if (profileCardRef.current) {
        await profileCardRef.current.save();
      }
    } catch {
      // save() already shows a toast on failure; still navigate through
    } finally {
      setContinuing(false);
      handleDismiss();
    }
  }, [handleDismiss]);

  const handleGenerate = async () => {
    if (!linkedinUrl && !companyWebsite) {
      toast({
        title: 'Enter at least one URL',
        description: 'Provide your LinkedIn URL and/or company website to generate your profile.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setStep('generating');
    try {
      const { data } = await api.post('/api/seller-profile/generate', {
        linkedin_url: linkedinUrl || null,
        company_website: companyWebsite || null,
      });
      setProfile(data);
      setStep('review');
    } catch (err) {
      toast({
        title: 'Generation failed',
        description: err.response?.data?.detail || err.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setStep('inputs');
    }
  };

  const firstName = user?.first_name || '';
  const lastName = user?.last_name || '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleDismiss}
      size={step === 'review' ? '2xl' : 'lg'}
      isCentered
      scrollBehavior="inside"
      closeOnOverlayClick={false}
    >
      <ModalOverlay bg="rgba(0,0,0,0.75)" backdropFilter="blur(4px)" />
      <ModalContent
        bg="#0f0f0f"
        border="1px solid rgba(255,255,255,0.10)"
        borderRadius="16px"
        mx={4}
        maxH="90vh"
      >
        <ModalCloseButton
          color="#525252"
          _hover={{ color: '#a3a3a3', bg: 'transparent' }}
          _focus={{ boxShadow: 'none' }}
          onClick={handleDismiss}
        />
        <ModalBody p={7}>
          <VStack spacing={6} align="stretch">

            {/* Header */}
            <VStack spacing={1} align="flex-start" pr={6}>
              <Heading
                size="md"
                color="#fafafa"
                fontWeight="700"
                letterSpacing="-0.02em"
              >
                {step === 'review' ? 'Review your seller profile' : 'Set up your seller profile'}
              </Heading>
              <Text fontSize="sm" color="#737373">
                {step === 'review'
                  ? 'Edit anything before saving — you can update this anytime from your profile page.'
                  : 'GillyReach uses your background to personalize outreach and find prospects that are a genuine fit for your practice.'}
              </Text>
            </VStack>

            {/* Progress dots */}
            <HStack spacing={2}>
              <Box
                h="3px"
                flex="1"
                bg={step !== 'inputs' ? '#fafafa' : 'rgba(255,255,255,0.15)'}
                borderRadius="full"
                transition="background 0.3s"
              />
              <Box
                h="3px"
                flex="1"
                bg={step === 'review' ? '#fafafa' : 'rgba(255,255,255,0.15)'}
                borderRadius="full"
                transition="background 0.3s"
              />
            </HStack>

            {/* Step: inputs */}
            {step === 'inputs' && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontSize="xs" color="#a3a3a3" mb={1.5} textTransform="uppercase" letterSpacing="0.06em">
                    LinkedIn Profile URL
                  </Text>
                  <Input
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    placeholder="https://linkedin.com/in/yourprofile"
                    {...inputProps}
                  />
                </Box>

                <Box>
                  <Text fontSize="xs" color="#a3a3a3" mb={1.5} textTransform="uppercase" letterSpacing="0.06em">
                    Company / Firm Website
                  </Text>
                  <Input
                    value={companyWebsite}
                    onChange={(e) => setCompanyWebsite(e.target.value)}
                    placeholder="https://yourfirm.com"
                    {...inputProps}
                  />
                </Box>

                <VStack spacing={2} align="stretch" pt={1}>
                  <Button
                    onClick={handleGenerate}
                    bg="#fafafa"
                    color="#0a0a0a"
                    borderRadius="full"
                    fontWeight="600"
                    fontSize="sm"
                    h="40px"
                    leftIcon={<Icon as={FiZap} />}
                    _hover={{ opacity: 0.85 }}
                    _focus={{ boxShadow: 'none' }}
                  >
                    Generate My Profile
                  </Button>
                  <Button
                    onClick={handleDismiss}
                    variant="ghost"
                    color="#525252"
                    fontSize="sm"
                    h="36px"
                    rightIcon={<Icon as={FiArrowRight} />}
                    _hover={{ color: '#a3a3a3', bg: 'transparent' }}
                    _focus={{ boxShadow: 'none' }}
                  >
                    Skip for now
                  </Button>
                </VStack>
              </VStack>
            )}

            {/* Step: generating */}
            {step === 'generating' && (
              <VStack spacing={5} align="center" py={6}>
                <Progress
                  size="xs"
                  isIndeterminate
                  colorScheme="whiteAlpha"
                  bg="rgba(255,255,255,0.08)"
                  borderRadius="full"
                  w="200px"
                />
                <VStack spacing={1}>
                  <Text fontSize="sm" color="#fafafa" fontWeight="500">
                    Building your profile...
                  </Text>
                  <Text fontSize="xs" color="#525252" textAlign="center">
                    Researching your firm and LinkedIn profile. This takes 10–20 seconds.
                  </Text>
                </VStack>
              </VStack>
            )}

            {/* Step: review */}
            {step === 'review' && profile && (
              <VStack spacing={4} align="stretch">
                <SellerProfileCard
                  ref={profileCardRef}
                  profile={profile}
                  onUpdate={(updated) => setProfile(updated)}
                  firstName={firstName}
                  lastName={lastName}
                  hideSaveButton
                />
                <Button
                  onClick={handleContinue}
                  isLoading={continuing}
                  loadingText="Saving..."
                  bg="#fafafa"
                  color="#0a0a0a"
                  borderRadius="full"
                  fontWeight="600"
                  fontSize="sm"
                  h="40px"
                  rightIcon={<Icon as={FiArrowRight} />}
                  _hover={{ opacity: 0.85 }}
                  _focus={{ boxShadow: 'none' }}
                >
                  Looks good, let's go
                </Button>
              </VStack>
            )}

          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
